import { useEffect, useRef, useCallback, type Dispatch, type SetStateAction } from 'react'
import type { NavigateFunction } from 'react-router-dom'
import { fetchWithAuth } from '@/lib/api'
import { isUuid, buildStoredDocumentUrl } from '@/lib/app-routes'
import type { OpenTab } from '@/components/features/document-tabs'
import { markProgrammaticNavigation } from '@/hooks/use-url-sync'

const DOCUMENT_TAB_TYPES = new Set<OpenTab['type']>(['note', 'work-spec', 'voice-memo'])

type DocMeta = {
  id?: string
  title?: string
  file_path?: string
  filePath?: string
  path?: string
}

async function fetchDocumentMeta(tab: OpenTab): Promise<DocMeta | null> {
  if (!DOCUMENT_TAB_TYPES.has(tab.type)) return null

  const pathHint = tab.filePath || (tab.id.includes('/') ? tab.id : null)

  if (isUuid(tab.id)) {
    const res = await fetchWithAuth(`/api/documents/${tab.id}`)
    if (res.ok) return res.json()
    if (pathHint) {
      const byPath = await fetchWithAuth(
        `/api/documents/by-path?path=${encodeURIComponent(pathHint)}`,
      )
      if (byPath.ok) return byPath.json()
    }
    return null
  }

  if (pathHint) {
    const res = await fetchWithAuth(
      `/api/documents/by-path?path=${encodeURIComponent(pathHint)}`,
    )
    if (res.ok) return res.json()
  }

  return null
}

function resolveFilePath(meta: DocMeta): string | null {
  return meta.file_path || meta.filePath || meta.path || null
}

export interface UseStructureRevisionSyncOptions {
  openTabs: OpenTab[]
  activeTabId: string | null
  setOpenTabs: Dispatch<SetStateAction<OpenTab[]>>
  setActiveTabId: (id: string | null) => void
  setActiveDocumentPath?: (path: string | null) => void
  navigate: NavigateFunction
  onBrowseRefresh?: () => void
}

/**
 * When another client renames/moves/creates/deletes documents, poll detects a structure
 * revision bump and refreshes browse + open tab titles/paths without closing tabs.
 */
export function useStructureRevisionSync({
  openTabs,
  activeTabId,
  setOpenTabs,
  setActiveTabId,
  setActiveDocumentPath,
  navigate,
  onBrowseRefresh,
}: UseStructureRevisionSyncOptions) {
  const syncingRef = useRef(false)
  const openTabsRef = useRef(openTabs)
  const activeTabIdRef = useRef(activeTabId)

  useEffect(() => {
    openTabsRef.current = openTabs
  }, [openTabs])

  useEffect(() => {
    activeTabIdRef.current = activeTabId
  }, [activeTabId])

  const syncFromServer = useCallback(async () => {
    if (syncingRef.current) return
    syncingRef.current = true
    try {
      onBrowseRefresh?.()

      const tabs = openTabsRef.current.filter((tab) => DOCUMENT_TAB_TYPES.has(tab.type))
      if (tabs.length === 0) return

      const updates: Array<{
        tabKey: string
        documentId: string
        oldPath: string | null
        newPath: string | null
        newTitle: string | null
        pathChanged: boolean
        titleChanged: boolean
      }> = []

      await Promise.all(
        tabs.map(async (tab) => {
          const meta = await fetchDocumentMeta(tab)
          if (!meta?.id) return

          const newPath = resolveFilePath(meta)
          const oldPath = tab.filePath || (tab.id.includes('/') ? tab.id : null)
          const newTitle = meta.title?.trim() || tab.title
          const pathChanged = Boolean(newPath && oldPath && newPath !== oldPath)
          const titleChanged = Boolean(newTitle && newTitle !== tab.title)

          if (!pathChanged && !titleChanged) return

          updates.push({
            tabKey: tab.tabSlotId || tab.id,
            documentId: meta.id,
            oldPath,
            newPath,
            newTitle,
            pathChanged,
            titleChanged,
          })
        }),
      )

      if (updates.length === 0) return

      setOpenTabs((prev) =>
        prev.map((tab) => {
          const hit = updates.find(
            (u) =>
              u.documentId === tab.id ||
              (u.oldPath && (tab.id === u.oldPath || tab.filePath === u.oldPath)),
          )
          if (!hit) return tab

          const next: OpenTab = { ...tab }
          if (hit.titleChanged && hit.newTitle) {
            next.title = hit.newTitle
          }
          if (hit.pathChanged && hit.newPath) {
            next.filePath = hit.newPath
            if (!isUuid(tab.id)) {
              next.id = hit.newPath
            }
          }
          return next
        }),
      )

      const active = activeTabIdRef.current
      const activeHit = updates.find((u) => {
        if (u.documentId === active) return true
        if (u.oldPath && active === u.oldPath) return true
        const activeTab = openTabsRef.current.find((t) => t.id === active)
        if (!activeTab) return false
        if (u.documentId === activeTab.id) return true
        if (u.oldPath && activeTab.filePath === u.oldPath) return true
        return false
      })

      if (activeHit?.pathChanged && activeHit.newPath) {
        const activeTab = openTabsRef.current.find((t) => t.id === active)
        const nextActiveId =
          activeTab && isUuid(activeTab.id) ? activeTab.id : activeHit.newPath

        markProgrammaticNavigation()
        setActiveTabId(nextActiveId)
        setActiveDocumentPath?.(activeHit.newPath)

        const urlTab = activeTab
          ? {
              ...activeTab,
              filePath: activeHit.newPath,
              ...(isUuid(activeTab.id) ? {} : { id: activeHit.newPath }),
            }
          : { id: nextActiveId, filePath: activeHit.newPath }
        navigate(buildStoredDocumentUrl(urlTab), { replace: true })
      } else if (activeHit?.titleChanged) {
        setActiveDocumentPath?.(activeHit.newPath || activeHit.oldPath)
      }
    } catch (err) {
      console.warn('[StructureRevisionSync] Failed to sync tabs:', err)
    } finally {
      syncingRef.current = false
    }
  }, [navigate, onBrowseRefresh, setActiveDocumentPath, setActiveTabId, setOpenTabs])

  useEffect(() => {
    let debounceTimer: ReturnType<typeof setTimeout> | null = null

    const onStructureRevision = () => {
      if (debounceTimer) clearTimeout(debounceTimer)
      debounceTimer = setTimeout(() => {
        void syncFromServer()
      }, 350)
    }

    window.addEventListener('flownotes-structure-revision', onStructureRevision)
    return () => {
      if (debounceTimer) clearTimeout(debounceTimer)
      window.removeEventListener('flownotes-structure-revision', onStructureRevision)
    }
  }, [syncFromServer])
}
